import { StyleSheet, Platform } from 'react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';

export default StyleSheet.create({
  safeContainer: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
  },
  container: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
  },

  stickyHeader: {
    backgroundColor: theme.backgroundPrimary,
    paddingBottom: 8,
  },
  stickySearchContainer: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },

  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.textPrimary,
    paddingHorizontal: 8,
    paddingLeft: 16,
    paddingVertical: 8,
    borderRadius: 28,
    gap: 12,
  },
  filterButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.backgroundPrimary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchPlaceholder: {
    flex: 1,
    ...Typography.body(theme.borderSubtle),
  },

  largeTrailCard: {
    backgroundColor: theme.backgroundSecondary,
    borderRadius: 24,
    overflow: 'hidden',
    marginBottom: 24,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
  },
  largeTrailImage: {
    width: '100%',
    height: 300,
    backgroundColor: theme.backgroundSecondary,
  },
  largeTrailImageScroll: {
    // content container for horizontal gallery — children use same height
    alignItems: 'stretch',
  },
  largeTrailImageContainer: {
    position: 'relative',
    width: '100%',
    height: 300,
    overflow: 'hidden',
  },
  largeTrailDots: {
    position: 'absolute',
    bottom: 12,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    alignItems: 'center',
    pointerEvents: 'none' as any,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  dotActive: {
    backgroundColor: theme.accentPrimary,
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  bookmarkButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(25, 30, 7, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: theme.backgroundSecondaryVarient,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  cardGradient: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  cardOverlayContent: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: 20,
  },
  cardContent: {
    padding: 16,
    paddingTop: 12,
  },
  cardTitle: {
    ...Typography.h2(theme.textPrimary),
    flex: 1,
    marginRight: 8,
  },
  cardLocation: {
    ...Typography.body(theme.textMuted),
    marginBottom: 12,
  },
  cardBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap' as const,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  overlayTrailName: {
    ...Typography.h2(theme.textPrimary),
    flex: 1,
    marginRight: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  overlayLocation: {
    ...Typography.body(theme.textMuted),
    marginBottom: 12,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  overlayBadges: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap' as const,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(25, 30, 7, 0.75)',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    gap: 4,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  badgeText: {
    ...Typography.caption(theme.textPrimary),
  },
  categoriesSection: {
    backgroundColor: theme.backgroundPrimary,
    paddingTop: 8,
    paddingBottom: 16,
  },
  categoriesScroll: {
    paddingHorizontal: 16,
    gap: 12,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 28,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    alignSelf: 'flex-start', 
    minHeight: 48,
    gap: 8,
  },

  categoryText: {
    ...Typography.body(theme.textMuted),
    ...Platform.select({
      android: {
        includeFontPadding: false,
        paddingRight: 10,
        minWidth: 50,
      },
    }),
  },
  categoryButtonActive: {
    backgroundColor: theme.accentPrimary,
    borderColor: theme.accentPrimary,
  },
  categoryTextActive: {
    color: theme.backgroundPrimary,
    fontWeight: '700',
  },
  categoryTrailsSection: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  mapViewContainer: {
    flex: 1,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  mapCard: {
    flex: 1,
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    backgroundColor: theme.backgroundSecondary,
  },
  mapEmptyOverlay: {
    position: 'absolute',
    top: 20,
    left: 20,
    right: 20,
    backgroundColor: theme.backgroundPrimary,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
  },
  mapEmptyText: {
    ...Typography.caption(theme.textMuted),
    textAlign: 'center',
  },
  mapLegend: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    backgroundColor: theme.backgroundPrimary,
    borderTopRightRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    gap: 6,
  },
  mapLegendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mapLegendPaw: {
    width: 18,
    height: 18,
  },
  mapLegendPill: {
    minWidth: 22,
    height: 18,
    paddingHorizontal: 5,
    borderRadius: 9,
    backgroundColor: theme.accentPrimary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapLegendPillText: {
    color: theme.backgroundPrimary,
    fontSize: 10,
    fontWeight: '700',
  },
  mapLegendText: {
    ...Typography.caption(theme.textPrimary),
    fontSize: 11,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  sectionTitle: {
    ...Typography.h2(theme.textSecondary),
  },
  bottomPadding: {
    height: 120,
  },
  floatingToggleButton: {
    position: 'absolute',
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 18,
    paddingVertical: 12,
    borderRadius: 28,
    backgroundColor: theme.accentPrimary,
    borderWidth: 1,
    borderColor: theme.accentPrimary,
    shadowColor: theme.backgroundSecondaryVarient,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 6,
  },
  floatingToggleText: {
    ...Typography.label(theme.backgroundPrimary),
    fontWeight: '700',
  },
  mapLoadingPill: {
    position: 'absolute',
    top: 12,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: theme.backgroundPrimary,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    shadowColor: theme.backgroundSecondaryVarient,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 3,
    minHeight: 36,
  },
  mapLoadingText: {
    ...Typography.caption(theme.textMuted),
    lineHeight: undefined,
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: 'center',
  },
  loadingText: {
    ...Typography.caption(theme.textMuted),
  },
  emptyContainer: {
    paddingVertical: 44,
    alignItems: 'center',
    gap: 16,
    paddingHorizontal: 32,
  },
  emptyText: {
    ...Typography.body(theme.textMuted),
    textAlign: 'center',
    lineHeight: 24,
  },
  placeholderImage: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.textPrimary,
  },
  searchModalContainer: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
  },
  searchModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.borderSubtle,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.accentPrimary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchModalInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.backgroundPrimary,
    borderRadius: 28,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: theme.textMuted,
    gap: 12,
  },
  searchModalInput: {
    flex: 1,
    ...Typography.body(theme.textPrimary),
    padding: 0,
  },
  clearButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.borderSubtle,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchModalContent: {
    flex: 1,
    paddingTop: 12,
  },
  currentLocationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 16,
    backgroundColor: theme.backgroundSecondary,
    borderRadius: 16,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  locationIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.accentPrimary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  locationTextContainer: {
    flex: 1,
  },
  locationTitle: {
    ...Typography.label(theme.textPrimary),
    marginBottom: 4,
  },
  locationSubtitle: {
    ...Typography.caption(theme.textMuted),
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 16,
    backgroundColor: theme.backgroundSecondary,
    borderRadius: 16,
    marginHorizontal: 16,
    marginBottom: 12,
  },
  suggestionIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.borderSubtle,
    justifyContent: 'center',
    alignItems: 'center',
  },
  suggestionTextContainer: {
    flex: 1,
  },
  suggestionTitle: {
    ...Typography.label(theme.textPrimary),
    marginBottom: 4,
  },
  suggestionSubtitle: {
    ...Typography.caption(theme.textMuted),
  },
});
